/**
 * SchemaEditorWrapper - 独立可用的 Schema Editor 包装组件
 * 负责加载 schema 数据并提供 EditorProvider 上下文
 */

import { useState, useEffect, useRef } from 'react';
import { EditorProvider } from '../../schema/hooks/useEditor';
import { EditorV2 } from './EditorV2';
import { initializeSchema } from '../../schema/core/schemaManager';
import { getUserSchemas } from '../../services/editorSchema';
import { getToken } from '../../services/api';
import type { SchemaInput } from '../../schema/core/types';
import type { NoteBlock } from '../../types';

const STORAGE_KEY = 'word-numbering-schema';
const ACTIVE_ID_KEY = 'word-numbering-schema-activeId';

async function loadSchema(forceLocal?: boolean, appliedSchemaType?: string, appliedSchemaId?: string): Promise<{ schema: SchemaInput; activeId?: number; editableMaxLevels?: Record<number, number> }> {
  // 如果是"应用模板"触发的重新加载，直接从 localStorage 读取（刚刚写入的）
  if (!forceLocal) {
    // 1. 优先按任务绑定的模板加载（统一从 getUserSchemas 查找）
    if (appliedSchemaId && getToken()) {
      try {
        const res = await getUserSchemas();
        const userSchema = res?.find((s: any) => s.schemaId === appliedSchemaId);
        if (userSchema) {
          const numberingSchemas = typeof userSchema.numberingSchemas === 'string'
            ? JSON.parse(userSchema.numberingSchemas) : userSchema.numberingSchemas;
          const paragraphStyles = userSchema.paragraphStyles
            ? (typeof userSchema.paragraphStyles === 'string' ? JSON.parse(userSchema.paragraphStyles) : userSchema.paragraphStyles)
            : undefined;
          const editableMaxLevels = userSchema.editableMaxLevels
            ? (typeof userSchema.editableMaxLevels === 'string' ? JSON.parse(userSchema.editableMaxLevels) : userSchema.editableMaxLevels)
            : undefined;
          const schemaData: any = { numberingSchemas };
          if (paragraphStyles) schemaData.paragraphStyles = paragraphStyles;
          if (editableMaxLevels) schemaData.editableMaxLevels = editableMaxLevels;
          console.log('Loaded schema from applied schema:', appliedSchemaId);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(schemaData));
          if (userSchema.selectedAbstractNumId != null) {
            localStorage.setItem(ACTIVE_ID_KEY, String(userSchema.selectedAbstractNumId));
          }
          return {
            schema: initializeSchema(schemaData),
            activeId: userSchema.selectedAbstractNumId ?? undefined,
            editableMaxLevels,
          };
        }
        console.warn('Applied schema not found, falling back to default logic');
      } catch (err) {
        console.warn('Failed to load applied schema, falling back:', err);
      }
    }

    // 2. 从后端 API 加载用户第一个 schema（按 sort_order ASC 排序，预设模板排前面）
    if (getToken()) {
      try {
        const res = await getUserSchemas();
        if (res && res.length > 0) {
          const latest = res[0];
          const numberingSchemas = typeof latest.numberingSchemas === 'string'
            ? JSON.parse(latest.numberingSchemas) : latest.numberingSchemas;
          const paragraphStyles = latest.paragraphStyles
            ? (typeof latest.paragraphStyles === 'string' ? JSON.parse(latest.paragraphStyles) : latest.paragraphStyles)
            : undefined;
          const editableMaxLevels = latest.editableMaxLevels
            ? (typeof latest.editableMaxLevels === 'string' ? JSON.parse(latest.editableMaxLevels) : latest.editableMaxLevels)
            : undefined;

          const schemaData: any = { numberingSchemas };
          if (paragraphStyles) schemaData.paragraphStyles = paragraphStyles;
          if (editableMaxLevels) schemaData.editableMaxLevels = editableMaxLevels;

          console.log('Loaded schema from API (schemaId:', latest.schemaId, ')');
          localStorage.setItem(STORAGE_KEY, JSON.stringify(schemaData));
          if (latest.selectedAbstractNumId != null) {
            localStorage.setItem(ACTIVE_ID_KEY, String(latest.selectedAbstractNumId));
          }
          return {
            schema: initializeSchema(schemaData),
            activeId: latest.selectedAbstractNumId ?? undefined,
            editableMaxLevels,
          };
        }
      } catch (err) {
        console.warn('Failed to load schema from API, falling back to localStorage:', err);
      }
    }
  }

  // 2. localStorage
  const savedSchema = localStorage.getItem(STORAGE_KEY);
  if (savedSchema) {
    try {
      const schema = JSON.parse(savedSchema);
      const savedActiveId = localStorage.getItem(ACTIVE_ID_KEY);
      console.log('Loaded schema from localStorage');
      return {
        schema: initializeSchema(schema),
        activeId: savedActiveId ? Number(savedActiveId) : undefined,
        editableMaxLevels: schema.editableMaxLevels,
      };
    } catch (error) {
      console.error('Failed to parse saved schema, loading from file:', error);
    }
  }

  // 3. Fallback: schema_output.json
  const timestamp = new Date().getTime();
  const response = await fetch(`./schema_output.json?t=${timestamp}`);
  const schema = await response.json();
  console.log('Loaded schema from schema_output.json');
  return { schema: initializeSchema(schema) };
}

interface SchemaEditorWrapperProps {
  meetingNotes?: NoteBlock[];
  originalNotes?: NoteBlock[];
  mode?: 'preview' | 'edit';
  onNotesChange?: (notes: NoteBlock[]) => void;
  taskId?: string | null;
  activeLinkId?: string | null;
  linkedIds?: Set<string>;
  onLinkToggle?: (id: string) => void;
  matchedNoteIds?: Set<string>;
  topLinkedId?: string | null;
  taskTitle?: string;
  /** 递增版本号，变化时重新从 localStorage 加载 schema（用于"应用模板"） */
  schemaVersion?: number;
  /** 模板中心"应用模板"回调 */
  onApplyTemplate?: (type?: string, schemaId?: string) => void;
  /** 任务绑定的模板类型 */
  appliedSchemaType?: string;
  /** 任务绑定的模板ID */
  appliedSchemaId?: string;
  /** 返回编辑纪要回调 */
  onBack?: () => void;
  /** 是否处于模板中心模式 */
  isTemplateCenter?: boolean;
  /** 切换模板中心/编辑纪要 */
  onToggleTemplateCenter?: () => void;
  /** 后端保存的编辑版纪要 */
  savedEditedNotes?: NoteBlock[] | null;
}

export function SchemaEditorWrapper({ meetingNotes, originalNotes, mode = 'preview', onNotesChange, taskId, activeLinkId, linkedIds, onLinkToggle, matchedNoteIds, topLinkedId, taskTitle, schemaVersion = 0, onApplyTemplate, appliedSchemaType, appliedSchemaId, onBack, isTemplateCenter, onToggleTemplateCenter, savedEditedNotes }: SchemaEditorWrapperProps) {
  const [schema, setSchema] = useState<SchemaInput | null>(null);
  const [activeId, setActiveId] = useState<number | undefined>(undefined);
  const [initialMaxLevels, setInitialMaxLevels] = useState<Record<number, number> | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);

  // 从模板中心切回预览时，重新加载已应用的模板（丢弃未应用的切换）
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current === 'edit' && mode === 'preview') {
      setReloadKey(k => k + 1);
    }
    prevModeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    setSchema(null); // 先清空，等加载完成后再设置
    const forceLocal = schemaVersion > 0 && !appliedSchemaId;
    loadSchema(forceLocal, appliedSchemaType, appliedSchemaId)
      .then(result => {
        setSchema(result.schema);
        if (result.activeId != null) setActiveId(result.activeId);
        if (result.editableMaxLevels) setInitialMaxLevels(result.editableMaxLevels);
      })
      .catch((err) => {
        console.error('Failed to load schema:', err);
        setError(String(err));
      });
  }, [schemaVersion, appliedSchemaType, appliedSchemaId, reloadKey]);

  if (error) {
    return (
      <div style={{ padding: 20, color: '#dc2626' }}>
        Schema 加载失败: {error}
      </div>
    );
  }

  if (!schema) {
    return (
      <div style={{ padding: 20, color: '#6b7280' }}>
        加载中...
      </div>
    );
  }

  const resolvedActiveId = activeId ?? (() => {
    const saved = localStorage.getItem('word-numbering-schema-activeId');
    return saved ? Number(saved) : 5;
  })();

  return (
    <EditorProvider key={`${schemaVersion}-${reloadKey}-${appliedSchemaId || ''}`} initialSchema={schema} initialAbstractNumId={resolvedActiveId} initialEditableMaxLevels={initialMaxLevels}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <EditorV2 meetingNotes={meetingNotes} originalNotes={originalNotes} mode={mode} onNotesChange={onNotesChange} taskId={taskId} activeLinkId={activeLinkId} linkedIds={linkedIds} onLinkToggle={onLinkToggle} matchedNoteIds={matchedNoteIds} topLinkedId={topLinkedId} taskTitle={taskTitle} onApplyTemplate={onApplyTemplate} appliedSchemaType={appliedSchemaType} appliedSchemaId={appliedSchemaId} onBack={onBack} isTemplateCenter={isTemplateCenter} onToggleTemplateCenter={onToggleTemplateCenter} savedEditedNotes={savedEditedNotes} />
      </div>
    </EditorProvider>
  );
}

export default SchemaEditorWrapper;
